import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { LocalPushNotification } from '@component/notification_manager/notification/local_push_notification';
import { NotificationService } from '@component/notification_manager/notification/notification.service';
import { getCompletedInformationGathering } from '@feature/information-gathering/information-gathering-navigation';
import { useResolve } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useBasket } from '../../hooks/use-basket';
import { CreateNewMessageAnalyticActions, useCreateNewMessage } from '../../hooks/use-create-new-message';

const logger = new Logger('useScreenRfv');

type ScreenState = 'ready' | 'not-ready' | 'busy';

export function useScreenRfv() {
	const createNewMessageController = useCreateNewMessage();
	const { addData, basket, reasonForVisitId: basketReasonForVisitId } = useBasket();
	const analyticService = useResolve(AnalyticService);
	const notificationService = useResolve(NotificationService);
	const location = useLocation();
	const informationGatheringSubmissionId = getCompletedInformationGathering(location.state)?.submissionId;

	const [screenState, setScreenState] = useState<ScreenState>('not-ready');
	const flowSettings = createNewMessageController.flowSettings;
	const filteredReasonForVisits = createNewMessageController.filteredReasonForVisits;

	const allowedToBeHere = useCallback((): boolean => {
		const flowData = createNewMessageController.flowData;
		return flowData.flowState === 'accessible' && flowData.value != null;
	}, [createNewMessageController]);

	const setReasonForVisit = useCallback(
		(reasonForVisitId: number | null) =>
			new Promise<void>((resolve, reject) => {
				logger.logInformation('Select reason for visit');

				if (flowSettings.flowState !== 'accessible') {
					return reject(new Error('Flow settings not accessible yet.'));
				}

				// Reuse only a product already carrying this journey's submission.
				if (
					basket != null &&
					basketReasonForVisitId === reasonForVisitId &&
					(basket.InformationGatheringSubmissionId ?? null) === (informationGatheringSubmissionId ?? null)
				) {
					return resolve();
				}

				return addData({
					reasonForVisitId,
					redRobinSessionId: flowSettings.value.redRobinSessionId,
					asAgentFor: flowSettings.value.asAgentFor,
					informationGatheringSubmissionId,
				})
					.then(resolve)
					.catch(() => {
						reject(new Error('User tried to post reason for visit'));
					});
			}),
		[flowSettings, basket, basketReasonForVisitId, addData, informationGatheringSubmissionId],
	);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			if (flowSettings.flowState !== 'accessible') {
				return;
			}

			analyticService.trackPageView({
				customTitle: 'Create new message: Reason for visit',
			});

			if (!allowedToBeHere()) {
				analyticService.trackEvent(
					AnalyticCategories.CreateNewMessage,
					CreateNewMessageAnalyticActions.NotAllowedToBeHere,
					'Reason for visit',
				);
				createNewMessageController.goToScreen('start');
				return;
			}

			if (filteredReasonForVisits.length === 0) {
				const reasonForVisit =
					flowSettings.flowState === 'accessible' ? (flowSettings.value.predefinedReasonForVisit?.[0] ?? null) : null;

				if (reasonForVisit) {
					analyticService.trackEvent(
						AnalyticCategories.CreateNewMessage,
						CreateNewMessageAnalyticActions.AutoSetReasonForVisit,
						`${reasonForVisit}`,
					);
				} else {
					analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.NoReasonForVisits);
				}

				if (cancelled) return;
				setScreenState('busy');

				await setReasonForVisit(reasonForVisit);
				if (cancelled) return;
				createNewMessageController.goToScreen('forms', { replace: true });
				return;
			}

			if (cancelled) return;
			setScreenState('ready');
		})();

		return () => {
			cancelled = true;
		};
	}, [analyticService, allowedToBeHere, createNewMessageController, filteredReasonForVisits.length, flowSettings, setReasonForVisit]);

	const selectReasonForVisit = async (reasonForVisitId: number) => {
		analyticService.trackEvent(
			AnalyticCategories.CreateNewMessage,
			CreateNewMessageAnalyticActions.SelectReasonForVisit,
			`${reasonForVisitId}`,
		);

		setScreenState('busy');
		void setReasonForVisit(reasonForVisitId)
			.then(() => {
				createNewMessageController.goToScreen('forms');
			})
			.catch((err) => {
				const message = err instanceof Error ? err.message : String(err);
				notificationService.add(
					new LocalPushNotification({
						label: 'Error',
						message,
						variant: 'danger',
						type: 'toast',
					}),
				);

				setScreenState('ready');
			});
	};

	return { screenState, selectReasonForVisit };
}
